import type { MutableRefObject } from 'react';
import { useEffect } from 'react';
import { Alert, Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { checkIfPmi } from '@/utils/commonUtils';
import { SELF_ENROLLED_BUILDING_ATTR } from '@/utils/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';
import type { Renter } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';

import { CoverageRequirementsAlert } from './content/invite-lease-details.drawer.coverage-requirements-alert';
import CoverageSelection from './content/invite-lease-details.drawer.coverage-selection';
import PmiCoverageSelection from './coverage-selection';
import { LeaseInformation } from './content/invite-lease-details.drawer.lease-information';
import { RenterContact } from './content/invite-lease-details.drawer.renter-contact';
import { InviteLeaseDetailsDrawerScreeningResults } from './content/invite-lease-details.drawer.screening-results';
import { InviteLeaseDetailsDrawerFormDivider } from './invite-lease-details.drawer.form';
import { ScreeningResult } from './constants';
import { calculateLeaseDurationMonths } from './utils/coverage.util';
import { useSmbCoverageOptions, useTrackCoveragePricingResolved } from './hooks';
import type { InviteFlowMilestoneKey } from './types';

// The invite drawer body, rendered inside <LeaseDetailsForm>'s FormProvider via
// `ui.slots.top`. It reuses the legacy section components verbatim — only the
// form handle changes from a locally-owned useForm to the engine's
// useFormContext. The RHF instance, schema, submit lifecycle, and sticky footer
// are owned by the engine (OTTERS-4716). Behavioral parity with
// InviteLeaseDetailsDrawerForm is the bar; keep the effects identical.

export type InviteLeaseDetailsSharedBodyProps = {
  user?: User;
  isSmbInvite: boolean;
  newCoveragesEnabled?: boolean;
  customCoverageDisabled?: boolean;
  selfEnrolledSDRLimit?: number;
  errorMsg: string;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
  /** Lifts address-derived self-enrolled state so the parent can size the SDR cap. */
  onSelfEnrolledChange: (isSelfEnrolled: boolean) => void;
  /** Written here, read by the parent's `buildPayload` (mirrors the legacy submit). */
  selectedSmbPricingRef: MutableRefObject<unknown>;
};

export const InviteLeaseDetailsSharedBody = ({
  user,
  isSmbInvite,
  newCoveragesEnabled,
  customCoverageDisabled = false,
  selfEnrolledSDRLimit,
  errorMsg,
  onMilestoneReached,
  onSelfEnrolledChange,
  selectedSmbPricingRef,
}: InviteLeaseDetailsSharedBodyProps) => {
  const { control, watch, setValue, trigger, resetField, formState } = useFormContext<Renter>();
  const { errors, isDirty, dirtyFields } = formState;

  // Mirror dirty state into the drawer store ref (drives close-confirmation).
  const dirtyStateRef = useInviteLeaseDetailsDrawerStore((state) => state.dirtyStateRef);
  dirtyStateRef.current = { isDirty, dirtyFields };

  const customCoverage = watch('customCoverage');
  const address = watch('address');

  useEffect(() => {
    // Skip when customCoverage is undefined (happens during re-renders with shouldUnregister: true)
    if (customCoverage === undefined) {
      return;
    }

    if (customCoverage) {
      resetField('approvalStatus');
      setValue('rentCoverage', RentCoverageTypes.Customize);
      setValue('securityDepositCoverage', SecurityDepositTypes.CustomizeOrRemove);
    } else {
      setValue('rentCoverage', RentCoverageTypes.CompanyDefault);
      setValue('securityDepositCoverage', SecurityDepositTypes.CompanyDefault);
    }
  }, [customCoverage, resetField, setValue]);

  useEffect(() => {
    onSelfEnrolledChange(address?.source === SELF_ENROLLED_BUILDING_ATTR);
  }, [address, onSelfEnrolledChange]);

  const leaseStart = watch('leaseStart');
  const leaseEnd = watch('leaseEnd');
  const leaseDurationMonths = calculateLeaseDurationMonths(leaseStart, leaseEnd);
  const monthsOfCoverage = watch('monthsOfCoverage');
  const selectedCoverageRuleId = watch('selectedCoverageRuleId');
  const grossMonthlyRent = watch('grossMonthlyRent');
  const freeRent = watch('freeRent');
  const prepaidRent = watch('prepaidRent');

  const {
    coverageOptions,
    selectedCoverageOption,
    isUsingDynamicPricing,
    isLoading: isCoverageOptionsLoading,
    isError: isCoverageOptionsError,
    error: coverageOptionsError,
  } = useSmbCoverageOptions({
    propertyId: address?.property_id,
    leaseStart,
    leaseEnd,
    grossMonthlyRent,
    freeRent,
    prepaidRent,
    leaseDurationMonths,
    selectedValue: monthsOfCoverage,
    selectedId: selectedCoverageRuleId,
    enabled: newCoveragesEnabled && isSmbInvite,
  });

  useTrackCoveragePricingResolved({
    propertyId: address?.property_id,
    enabled: isSmbInvite && isUsingDynamicPricing,
    isLoading: isCoverageOptionsLoading,
    isError: isCoverageOptionsError,
    error: coverageOptionsError,
    hasCustomScreeningStatusLabels: false,
    pricingOptionCount: coverageOptions?.length ?? 0,
    pricingData: coverageOptions,
  });

  // Surface the resolved pricing to the engine's buildPayload (the legacy form
  // injected `selectedSmbPricing` into the submit payload directly).
  useEffect(() => {
    selectedSmbPricingRef.current = selectedCoverageOption?.pricing;
  }, [selectedCoverageOption, selectedSmbPricingRef]);

  useEffect(() => {
    if (!newCoveragesEnabled || !isSmbInvite) return;

    if (!monthsOfCoverage) {
      setValue('approvalStatus', '', { shouldValidate: false });
      setValue('selectedCoverageRuleId', undefined);
      return;
    }

    if (!selectedCoverageOption || selectedCoverageOption.disabled) {
      setValue('approvalStatus', '', { shouldValidate: false });
      setValue('monthsOfCoverage', undefined);
      setValue('selectedCoverageRuleId', undefined);
      return;
    }

    if (customCoverage) {
      setValue('approvalStatus', ScreeningResult.Denied, { shouldValidate: false });
      return;
    }

    if (watch('approvalStatus') !== selectedCoverageOption.screeningResult) {
      setValue('approvalStatus', selectedCoverageOption.screeningResult, { shouldValidate: false });
    }
  }, [
    customCoverage,
    isSmbInvite,
    monthsOfCoverage,
    newCoveragesEnabled,
    selectedCoverageOption,
    setValue,
    watch,
  ]);

  const showPmiCoverage = Boolean(user && checkIfPmi(user));
  const showCoverageSection =
    (isSmbInvite && !!customCoverage) || (!isSmbInvite && !customCoverageDisabled);

  // Single Box wrapper so the engine's <Stack spacing> treats the whole body as
  // one child — the legacy per-element spacing inside is preserved instead of
  // being flattened into the engine's uniform gap.
  return (
    <Box>
      {customCoverageDisabled && <CoverageRequirementsAlert />}
      <LeaseInformation
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        trigger={trigger}
        onMilestoneReached={onMilestoneReached}
      />
      <InviteLeaseDetailsDrawerFormDivider />
      <RenterContact control={control} errors={errors} watch={watch} trigger={trigger} />
      <InviteLeaseDetailsDrawerFormDivider />
      <InviteLeaseDetailsDrawerScreeningResults
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        isSmbInvite={isSmbInvite}
        newCoveragesEnabled={newCoveragesEnabled}
        onMilestoneReached={onMilestoneReached}
        coverageLengthOptions={coverageOptions}
        showCoverageSkeleton={Boolean(
          isSmbInvite && newCoveragesEnabled && isCoverageOptionsLoading,
        )}
      />
      {showCoverageSection && (
        <>
          <InviteLeaseDetailsDrawerFormDivider />
          {showPmiCoverage ? (
            <PmiCoverageSelection
              control={control}
              errors={errors}
              selfEnrolledSDRLimit={selfEnrolledSDRLimit}
            />
          ) : (
            <CoverageSelection
              control={control}
              errors={errors}
              watch={watch}
              useDrawerStyles
              selfEnrolledSDRLimit={selfEnrolledSDRLimit}
            />
          )}
        </>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ marginY: 2 }}>
          {errorMsg}
        </Alert>
      )}
    </Box>
  );
};
